import React, { useState } from "react";
import { Button, Modal, Form, Divider, Spin } from "antd";
import { $t } from "~/i18n.js";
import { timeToMinutes } from "~/lib/localize.js";
import { TacometerInput } from "./TacometerInput.jsx";
import { FlightMotorTime } from "./FlightMotorTime.jsx";
import { AddButton } from "../buttons/AddButton.jsx";
import { api } from "~/lib/api.js";
import { DateFormat } from "../date/DateFormat.jsx";
import { Alert } from "antd";

export const FlightTacometer = ({ motor_format, flight, saveFlight, skipTitle, readOnly }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [beginMinutes, setBeginMinutes] = useState(0);
  const [form] = Form.useForm();

  const [loadingMotorData, setLoadingMotorData] = useState(false);
  const [motorTimeItems, SetMotorTimeItems] = useState([]);

  const hasMotorHours = flight.motor_minutes > 0;
  const vluchtDuur = (flight.blocktime > 0 ? flight.blocktime : (flight.vluchtduur > 0 ? flight.vluchtduur : 0));

  const fetchMotorData = async () => {
    if (!flight?.kist_id) return;

    setLoadingMotorData(true);
    try {
      const data = await api.post("materiaal/kist_motor_minutes.json", {
        kist_id: flight.kist_id,
        date: flight.datum,
      });
      if (data) createMotorTimeItems(data);
    } catch (error) {
      console.error("Error fetching motor data:", error);
    } finally {
      setLoadingMotorData(false);
    }
  };
  
  const createMotorTimeItems = (data) => {
    const items = [];
    const currentDate = flight.datum;
    const currentFlightStartMinutes = timeToMinutes(flight.start_tijd);
    
    // 1. Add flights from current date (sorted by start time)
    const todayFlights = (data.flights || [])
      .filter(
        (f) => {
          if (f.uuid === flight.uuid) return false; // Exclude current flight
          if (flight.start_tijd) {
            const flightStartMinutes = timeToMinutes(f.start_tijd);
            // Include only flights that started before the current flight
            return f.datum === currentDate && flightStartMinutes > 0 && flightStartMinutes <= currentFlightStartMinutes;
          }
          return f.datum === currentDate
        }
      )
      .sort(
        (a, b) => timeToMinutes(b.start_tijd) - timeToMinutes(a.start_tijd)
      );

    todayFlights.forEach((f) => {
      items.push({
        type: "flight",
        datum: f.datum,
        start_tijd: f.start_tijd,
        motor_end: f.motor_end,
        motor_minutes: f.motor_minutes,
        id: f.id,
      });
    });

    // 2. Add inspections from current date
    const todayInspections = (data.inspections || []).filter(
      (i) => i.datum === currentDate
    );

    todayInspections.forEach((i) => {
      if (i.total_motor_minutes > 0) {
        items.push({
          type: "inspection",
          datum: i.datum,
          start_tijd: null,
          motor_end: i.total_motor_minutes,
          motor_minutes: 0,
          id: `inspection-${i.id}`,
        });
      }
    });

    // 3. Add flights from previous days
    const previousFlights = (data.flights || []).filter(
      (f) => f.datum !== currentDate
    );
    
    previousFlights.forEach((f) => {
      items.push({
        type: "flight",
        datum: f.datum,
        start_tijd: f.start_tijd,
        motor_end: f.motor_end,
        motor_minutes: f.motor_minutes,
        id: f.id,
      });
    });

    SetMotorTimeItems(items);

    // Set begin motor time from first item that has motor time
    const firstItemWithMotorTime = items.find((item) => item.motor_end > 0);
    if (firstItemWithMotorTime && !flight.motor_end) {
      setBeginMinutes(firstItemWithMotorTime.motor_end);
      form.setFieldValue("begin_minutes", firstItemWithMotorTime.motor_end);
      form.setFieldValue("motor_end", firstItemWithMotorTime.motor_end);
    }
  };

  const handleSetMotorTime = (item) => {
    setBeginMinutes(item.motor_end);
    form.setFieldValue("begin_minutes", item.motor_end);

    // Recalculate used motor time if end motor time is already set
    const currentEndTime = form.getFieldValue("motor_end");
    if (currentEndTime > 0) {
      const calculatedUsedTime = currentEndTime - item.motor_end;
      form.setFieldValue("motor_minutes", Math.max(0, calculatedUsedTime));
    }
  };

  const handleEndMotorChange = (value) => {
    const beginTime = form.getFieldValue("begin_minutes");
    
    // Ensure end time is not less than begin time
    if (beginTime > 0 && value < beginTime) {
      // Don't update if end time would be less than begin time
      return;
    }
    
    form.setFieldValue("motor_end", value);

    // Calculate used motor time if begin time is set
    if (beginTime > 0 && value > 0) {
      const calculatedUsedTime = value - beginTime;
      form.setFieldValue("motor_minutes", Math.max(0, calculatedUsedTime));
    }
  };

  const handleUsedMotorChange = (value) => {
    form.setFieldValue("motor_minutes", value);

    // Calculate end motor time if begin time is set
    const beginTime = form.getFieldValue("begin_minutes");
    if (beginTime >= 0 && value >= 0) {
      const calculatedEndTime = beginTime + value;
      form.setFieldValue("motor_end", calculatedEndTime);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);

    // Derive begin minutes from flight data if motor_end is set
    let derivedBeginMinutes = beginMinutes;
    if (flight.motor_end > 0 && flight.motor_minutes > 0) {
      derivedBeginMinutes = flight.motor_end - flight.motor_minutes;
      setBeginMinutes(derivedBeginMinutes);
    }

    // Set initial form values
    form.setFieldsValue({
      motor_end: flight.motor_end || 0,
      motor_minutes: flight.motor_minutes || 0,
      begin_minutes: derivedBeginMinutes,
    });
    fetchMotorData();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (saveFlight) {
        saveFlight({
          motor_end: values.motor_end,
          motor_minutes: values.motor_minutes,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    form.setFieldsValue({
      motor_end: flight.motor_end || 0,
      motor_minutes: flight.motor_minutes || 0,
      begin_minutes: beginMinutes,
    });
    setIsModalOpen(false);
  };

  const motor_end = Form.useWatch("motor_end", form);
  const begin_minutes = Form.useWatch("begin_minutes", form);
  
  return (
    <>
      <div className="row">
        {!skipTitle && <p style={{ width: 80 }}>{$t("Motor uren")}:</p>}
        
        {hasMotorHours && (
          <FlightMotorTime motorMinutes={flight.motor_minutes} />
        )}

        {!hasMotorHours && (
          <AddButton size="medium" onClick={handleModalOpen} disabled={readOnly} />
        )}

        {hasMotorHours && (
          <Button
            size="small"
            onClick={handleModalOpen}
            style={{ marginLeft: 8 }}
            disabled={readOnly}
          >
            {$t("Wijzig")}
          </Button>
        )}
      </div>

      <Modal
        title={$t("Motor uren bewerken")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={$t("Opslaan")}
        cancelText={$t("Annuleer")}
        width={450}
      >
        <Form
          form={form}
          layout="vertical"
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 24 }}
          initialValues={{
            motor_end: flight.motor_end || 0,
            motor_minutes: flight.motor_minutes || 0,
            begin_minutes: beginMinutes,
          }}
        >



          <Form.Item label={$t("Beginstand motor")} name="begin_minutes">
            <TacometerInput
              motor_format={motor_format}
              value={form.getFieldValue("begin_minutes")}
              onChange={(value) => form.setFieldValue("begin_minutes", value)}
              />
          </Form.Item>
          
          <Form.Item
            label={$t("Gebruikte motor tijd")}
            name="motor_minutes"
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  if (vluchtDuur > 0 && value > vluchtDuur) {
                    return Promise.reject(new Error($t("Gebruikte motor tijd mag niet groter zijn dan de vluchtduur")));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <TacometerInput
              motor_format={motor_format}
              readOnly={false}
              value={form.getFieldValue("motor_minutes")}
              onChange={handleUsedMotorChange}
            />
          </Form.Item>


          
          <Form.Item
            label={$t("Eindstand motor")}
            name="motor_end"
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  const beginTime = form.getFieldValue("begin_minutes");
                  if (beginTime > 0 && value < beginTime) {
                    return Promise.reject(new Error($t("Eindstand motor moet groter zijn aan beginstand motor")));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <TacometerInput
              motor_format={motor_format}
              readOnly={false}
              value={motor_end}
              onChange={handleEndMotorChange}
            />
          </Form.Item>


            {beginMinutes !== null && beginMinutes !== begin_minutes && <Alert style={{marginTop: 12}} type='error' message={$t('Let op, de ingevulde begintijd is gewijzigd. Check of dit klopt.')} />}
        </Form>

        <b className="title label">{$t("Laatste motor tijd")}</b>

        {loadingMotorData && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin size="large" />
          </div>
        )}

        {motorTimeItems.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {motorTimeItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom:
                    index < motorTimeItems.length - 1
                      ? "1px solid #f0f0f0"
                      : "none",
                }}
              >
                <div className="row" style={{ gap: 6 }}>
                  <Button
                    size="small"
                    disabled={item.motor_end === beginMinutes}
                    onClick={() => handleSetMotorTime(item)}
                  >
                    {$t("Zet tijd")}
                  </Button>

                  <div style={{ width: 75 }}>
                  <DateFormat
                    date={item.datum}
                    options={{
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                    }}
                  />
                  </div>

                  <div style={{ width: 80 }}>
                    {item.start_tijd && <span>{item.start_tijd}</span>}
                    {item.type === "inspection" && (
                      <span style={{ fontStyle: "italic" }}>{$t("Inspectie")}</span>
                    )}
                  </div>
                  <FlightMotorTime motorMinutes={item.motor_end} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loadingMotorData && motorTimeItems.length === 0 && (
          <p style={{ textAlign: "center", color: "#999", padding: 20 }}>
            {$t("Geen motor tijden gevonden")}
          </p>
        )}
      </Modal>
    </>
  );
};
